import { agentApi } from "@/services/agentApi";

export const createPlaygroundSlice = (set, get) => ({
  chatMessages: [],
  isProcessingMessage: false,
  chatError: null,
  // Initialize chat with a welcome message
  initializeChat: (
    welcomeMessage = "This is a playground for testing your assistant. Send a message to start the conversation."
  ) => {
    set({
      chatMessages: [{ role: "system", content: welcomeMessage }],
      chatError: null,
    });
  },

  addUserMessage: (message) => {
    const isString = typeof message === "string";
    const msg = isString
      ? { role: "user", content: message }
      : {
          role: "user",
          content: message.content || "",
          attachments: message.attachments || [],
          uploading: message.uploading || false,
          tempId: message.tempId,
          error: message.error,
        };

    set((state) => ({
      chatMessages: [...state.chatMessages, msg],
    }));
    return msg;
  },

  addAssistantMessage: (message) => {
    const isString = typeof message === "string";
    const msg = isString
      ? { role: "assistant", content: message }
      : {
          role: "assistant",
          content: message.content || "",
          attachments: message.attachments || [],
        };
    set((state) => ({
      chatMessages: [...state.chatMessages, msg],
    }));
    return msg;
  },

  updateMessageByTempId: (tempId, patch) => {
    if (!tempId) return;
    set((state) => ({
      chatMessages: state.chatMessages.map((m) =>
        m.tempId && m.tempId === tempId ? { ...m, ...patch } : m
      ),
    }));
  },

  sendMessage: async (message, agentId, errorMessages = {}) => {
    if (!message || !agentId) return null;

    const defaultErrorMessage =
      errorMessages.noResponse ||
      "I couldn't generate a response at this time.";

    let optimisticAttachments = [];

    try {
      set({ isProcessingMessage: true, chatError: null });

      if (typeof message === "string") {
        get().addUserMessage(message);
        const response = await agentApi.playground(message, null, agentId);
        const assistantResponse = response?.result || defaultErrorMessage;
        get().addAssistantMessage(assistantResponse);
        set({ isProcessingMessage: false });
        return assistantResponse;
      }

      const { text, files } = message;
      const hasFiles = files && Array.isArray(files) && files.length > 0;
      const hasText = text && text.trim();

      if (!hasText && !hasFiles) {
        set({ isProcessingMessage: false });
        return null;
      }

      const tempId = `temp-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 9)}`;

      optimisticAttachments = hasFiles
        ? files.map((file) => ({
            url: URL.createObjectURL(file),
            uploading: true,
            fileName: file.name,
          }))
        : [];

      get().addUserMessage({
        content: text || "",
        attachments: optimisticAttachments,
        uploading: hasFiles,
        tempId,
      });

      const response = await agentApi.playground(text, files, agentId);
      const assistantResponse = response?.result || defaultErrorMessage;

      if (hasFiles) {
        // mark attachments as finished uploading in the optimistic message
        get().updateMessageByTempId(tempId, { uploading: false });
      }

      get().addAssistantMessage(assistantResponse);
      set({ isProcessingMessage: false });

      return assistantResponse;
    } catch (error) {
      console.error("Error in playground chat:", error);
      set({
        chatError: error.message,
        isProcessingMessage: false,
      });

      const errorMessage = `${errorMessages.error || "Error"}: ${
        error.message
      }`;
      get().addAssistantMessage(errorMessage);

      return null;
    } finally {
      // Ensure any optimistic object URLs are revoked to avoid leaking blob URLs
      if (optimisticAttachments && optimisticAttachments.length > 0) {
        optimisticAttachments.forEach((att) => {
          try {
            if (att && att.url) {
              URL.revokeObjectURL(att.url);
            }
          } catch (e) {
            // Structured logging to aid debugging
            console.error(
              "Failed to revoke optimistic object URL",
              { url: att && att.url },
              e
            );
          }
        });
      }
    }
  },
});
